/**
 * Calendrier local sans fuseau (EF-CAL-002, annexe A).
 *
 * `Date` est proscrit pour ces valeurs : il porte un instant absolu et applique
 * le fuseau de l'appareil à l'analyse comme au formatage. Un changement de
 * fuseau ou un passage à l'heure d'hiver décalerait silencieusement toutes les
 * heures de passage enregistrées.
 *
 * Le type retenu compte les secondes depuis 1970-01-01T00:00:00 dans un
 * calendrier proleptique grégorien, sans aucune notion de fuseau.
 */

declare const brand: unique symbol;

export type LocalDateTime = number & { readonly [brand]: 'LocalDateTime' };

export interface CalendarFields {
  readonly year: number;
  readonly month: number; // 1-12
  readonly day: number; // 1-31
  readonly hour: number; // 0-23
  readonly minute: number; // 0-59
  readonly second: number; // 0-59
}

export const SECONDS_PER_MINUTE = 60;
export const SECONDS_PER_HOUR = 3600;
export const SECONDS_PER_DAY = 86400;

/**
 * Nombre de jours depuis l'époque, algorithme de Howard Hinnant.
 * Valide pour tout calendrier proleptique grégorien.
 */
export function daysFromCivil(year: number, month: number, day: number): number {
  const y = year - (month <= 2 ? 1 : 0);
  const era = Math.floor(y / 400);
  const yoe = y - era * 400; // [0, 399]
  const doy = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1; // [0, 365]
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy; // [0, 146096]
  return era * 146097 + doe - 719468;
}

/** Réciproque de `daysFromCivil`. */
export function civilFromDays(days: number): { year: number; month: number; day: number } {
  const z = days + 719468;
  const era = Math.floor(z / 146097);
  const doe = z - era * 146097; // [0, 146096]
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const y = yoe + era * 400;
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153); // [0, 11]
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1; // [1, 31]
  const m = mp + (mp < 10 ? 3 : -9); // [1, 12]
  return { year: y + (m <= 2 ? 1 : 0), month: m, day: d };
}

export function fromFields(fields: CalendarFields): LocalDateTime {
  const days = daysFromCivil(fields.year, fields.month, fields.day);
  return (days * SECONDS_PER_DAY +
    fields.hour * SECONDS_PER_HOUR +
    fields.minute * SECONDS_PER_MINUTE +
    fields.second) as LocalDateTime;
}

export function toFields(value: LocalDateTime): CalendarFields {
  const days = Math.floor(value / SECONDS_PER_DAY);
  const rest = value - days * SECONDS_PER_DAY;
  const civil = civilFromDays(days);
  return {
    year: civil.year,
    month: civil.month,
    day: civil.day,
    hour: Math.floor(rest / SECONDS_PER_HOUR),
    minute: Math.floor((rest % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE),
    second: Math.floor(rest % SECONDS_PER_MINUTE),
  };
}

export function addSeconds(value: LocalDateTime, delta: number): LocalDateTime {
  return (value + delta) as LocalDateTime;
}

export function localDateTime(value: number): LocalDateTime {
  return value as LocalDateTime;
}

/** Secondes écoulées depuis minuit du jour civil contenant `value`. */
export function secondsSinceMidnight(value: LocalDateTime): number {
  const days = Math.floor(value / SECONDS_PER_DAY);
  return value - days * SECONDS_PER_DAY;
}

/** Minuit du jour civil contenant `value`. */
export function startOfDay(value: LocalDateTime): LocalDateTime {
  return (Math.floor(value / SECONDS_PER_DAY) * SECONDS_PER_DAY) as LocalDateTime;
}

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n));
const pad4 = (n: number): string => String(n).padStart(4, '0');

/** Format `YYYY-MM-DDTHH:mm:ss` de l'annexe A. Aucun fuseau, aucun décalage. */
export function formatLocalDateTime(value: LocalDateTime): string {
  const f = toFields(value);
  return `${pad4(f.year)}-${pad2(f.month)}-${pad2(f.day)}T${pad2(f.hour)}:${pad2(f.minute)}:${pad2(f.second)}`;
}

const LOCAL_DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/** Analyse `YYYY-MM-DDTHH:mm:ss`. Retourne `null` si la chaîne est non conforme. */
export function parseLocalDateTime(text: string): LocalDateTime | null {
  const match = LOCAL_DATE_TIME_PATTERN.exec(text);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = match[6] === undefined ? 0 : Number(match[6]);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  if (hour > 23 || minute > 59 || second > 59) return null;
  const value = fromFields({ year, month, day, hour, minute, second });
  // Rejette les dates civiles impossibles, par exemple le 31 février.
  const back = toFields(value);
  if (back.year !== year || back.month !== month || back.day !== day) return null;
  return value;
}

const TIME_OF_DAY_PATTERN = /^(\d{2}):(\d{2})$/;

/** Analyse `HH:mm` et retourne les secondes depuis minuit. */
export function parseTimeOfDay(text: string): number | null {
  const match = TIME_OF_DAY_PATTERN.exec(text);
  if (match === null) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return hour * SECONDS_PER_HOUR + minute * SECONDS_PER_MINUTE;
}

export function formatTimeOfDay(secondsOfDay: number): string {
  const h = Math.floor(secondsOfDay / SECONDS_PER_HOUR);
  const m = Math.floor((secondsOfDay % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  return `${pad2(h)}:${pad2(m)}`;
}

/** Vrai lorsque les deux instants tombent le même jour civil. */
export function isSameDay(a: LocalDateTime, b: LocalDateTime): boolean {
  return Math.floor(a / SECONDS_PER_DAY) === Math.floor(b / SECONDS_PER_DAY);
}

/**
 * Horodatage technique RFC 3339 avec décalage UTC. Réservé à `createdAt` et
 * `updatedAt` de l'annexe A, qui représentent bien des instants absolus.
 */
export function nowRfc3339(): string {
  return new Date().toISOString();
}

const RFC3339_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

export function isRfc3339(text: string): boolean {
  return RFC3339_PATTERN.test(text) && !Number.isNaN(Date.parse(text));
}

/**
 * Instant courant lu comme heure murale locale de l'appareil.
 *
 * C'est le seul point du code où `Date` sert à produire un `LocalDateTime` :
 * il s'agit de capturer une fois les champs de calendrier affichés par
 * l'appareil, non de conserver un instant absolu.
 */
export function nowLocalDateTime(): LocalDateTime {
  const now = new Date();
  return fromFields({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
    hour: now.getHours(),
    minute: now.getMinutes(),
    second: now.getSeconds(),
  });
}
